import { ChangeDetectionStrategy, Component, OnInit, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';

import { debounceTime, Subject, takeUntil } from 'rxjs';
import { TreeNode } from 'primeng/api';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { TreeModule } from 'primeng/tree';
import { InputTextModule } from 'primeng/inputtext';
import { ButtonModule } from 'primeng/button';

import { IOrganizationUnit } from '@src/app/interfaces/api/organization-units.interface';
import { Destroyable } from '@src/app/utils/Destroyable';

interface IData {
  units: IOrganizationUnit[];
}

@Component({
  selector: 'app-tree-selection-objects',
  standalone: true,
  imports: [TreeModule, FormsModule, InputTextModule, ButtonModule],
  templateUrl: './tree-selection-objects.component.html',
  styleUrls: ['./tree-selection-objects.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TreeSelectionObjectsComponent extends Destroyable implements OnInit {

  private originalTreeNodes: TreeNode[] = [];
  public filteredTreeNodes = signal<TreeNode[]>([]);

  public searchTerm: string;
  public selectedGroup: IOrganizationUnit;

  public onChangeSearchValue$: Subject<string> = new Subject();

  constructor(
    public config: DynamicDialogConfig<IData>,
    public ref: DynamicDialogRef,
  ) {
    super();
  }

  ngOnInit(): void {
    this.onChangeSerachValue();
    this.originalTreeNodes = this.mapUnitsToTreeNodes(this.config.data?.units || []);
    this.filteredTreeNodes.set(this.originalTreeNodes);
  }

  public cancel(): void {
    this.ref.close();
  }

  public confirm(): void {
    if (this.selectedGroup) {
      this.ref.close(this.selectedGroup);
      return;
    }
    alert('Zaznacz grupę, aby kontynuować.');
  }

  public onSelectGroup(group: TreeNode<IOrganizationUnit>): void {
    this.selectedGroup = {
      ...group.data!
    };
  }

  private onChangeSerachValue(): void {
    this.onChangeSearchValue$
    .pipe(
      takeUntil(this.destroy$),
      debounceTime(300),
    )
    .subscribe({
      next: () => {
        this.filterTree();
      }
    });
  }

  private mapUnitsToTreeNodes(units: IOrganizationUnit[], isRoot = true): TreeNode[] {
    return units.map(unit => ({
      label: unit.Name,
      key: unit.Id.toString(),
      data: unit,
      expanded: isRoot,
      children: this.mapUnitsToTreeNodes(unit.SubUnits || [], false),
    }));
  }

  private filterTree(): void {
    const term = this.searchTerm.toLowerCase();

    if (!term) {
      this.filteredTreeNodes.set(this.originalTreeNodes);
    }
    else {
      const result = this.deepFilterTree(this.originalTreeNodes, term);
      this.filteredTreeNodes.set(result);
    }
  }

  private deepFilterTree(nodes: TreeNode[], term: string): TreeNode[] {
    const filtered: TreeNode[] = [];

    for (const node of nodes) {
      const labelMatch = node.label?.toLowerCase().includes(term);
      const children = node.children ? this.deepFilterTree(node.children, term) : [];

      if (labelMatch || children.length > 0) {
        filtered.push({
          ...node,
          children: children,
        });
      }
    }

    return filtered;
  }

}
